import { Component, computed, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { CellValueChangedEvent, ColDef, GridOptions } from 'ag-grid-community';
import { Debounce } from 'lodash-decorators';
import { DateTime } from 'luxon';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { forkJoin } from 'rxjs';
import { PanModalComponent } from '../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../error.utils';
import { defaultTableDefOptions } from '../../helpers';
import { BranchService } from '../../service/branch.service';
import { LocationService } from '../../service/location.service';
import { NotifyService } from '../../service/notify.service';
import { Branch, BranchShrinkData, LocationResponse } from '../../types';
import { ManageShrinkListActionsComponent } from './manage-shrink-list-actions/manage-shrink-list-actions.component';
import { ManageShrinkListHeaderActionsComponent } from './manage-shrink-list-header-actions/manage-shrink-list-header-actions.component';

@Component({
  selector: 'app-manage-shrink',
  templateUrl: 'manage-shrink.component.html',
  styleUrls: ['./manage-shrink.component.scss'],
  standalone: false,
})
export class ManageShrinkPageComponent implements OnInit {
  private branchService = inject(BranchService);
  private locationService = inject(LocationService);
  private notifyService = inject(NotifyService);

  public branchId = input<number | undefined>();
  public branchData = input<Branch>();

  public isLoading = signal<boolean>(false);
  public branch = signal<Branch | undefined>(undefined);
  public allBranchLocations = signal<LocationResponse[]>([]);
  public monthAndYear = linkedQueryParam<Date>('period', {
    parse: (value) => (value ? DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate() : DateTime.now().toJSDate()),
    stringify: (value) => DateTime.fromJSDate(value).toFormat('yyyy-MM-dd'),
  });

  public selectableShrinkLocations = computed(() => {
    return this.allBranchLocations().filter((location) => {
      return !this.shrinkData().some((shrink) => shrink.locationId === location.id);
    });
  });

  public shrinkModal = viewChild<PanModalComponent>('addModal');
  public shrinkAddLocation = signal<LocationResponse | undefined>(undefined);
  public shrinkAddPercentage = signal<number | undefined>(undefined);

  public shrinkData = signal<BranchShrinkData[]>([]);

  public readonly tableDef: GridOptions = {
    ...defaultTableDefOptions(),
    onCellValueChanged: (event: CellValueChangedEvent<BranchShrinkData>) => {
      this.updateShrink(event.data);
    },
    pagination: true,
    paginationPageSize: 20,
  };

  public readonly columnDefs: ColDef<BranchShrinkData>[] = [
    {
      field: 'locationId',
      headerName: 'Location ID',
      cellDataType: 'number',
      sortable: false,
      filter: 'agNumberColumnFilter',
      maxWidth: 150,
    },
    {
      field: 'externalId',
      headerName: 'External ID',
      cellDataType: 'text',
      sortable: false,
      filter: 'agTextColumnFilter',
      maxWidth: 150,
    },
    {
      field: 'locationName',
      headerName: 'Location Name',
      cellDataType: 'text',
      sortable: true,
      filter: 'agTextColumnFilter',
      sort: 'asc',
    },
    {
      field: 'shrinkPercent',
      headerName: 'Shrink %',
      cellDataType: 'number',
      editable: true,
      sortable: true,
      maxWidth: 100,
    },
    {
      headerName: '',
      suppressKeyboardEvent: () => true,
      suppressColumnsToolPanel: true,
      sortable: false,
      width: 100,
      maxWidth: 100,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      headerComponent: ManageShrinkListHeaderActionsComponent,
      headerClass: ['w-full'],
      headerComponentParams: {
        addCallback: () => {
          this.shrinkModal().show();
        },
      },
      cellRenderer: ManageShrinkListActionsComponent,
      cellRendererParams: {
        removeCallback: (data: BranchShrinkData) => {
          this.deleteShrink(data);
        },
      },
    },
  ];

  ngOnInit() {
    if (!this.branchId()) {
      return;
    }

    this.loadBranch();
    this.loadShrinkData();
  }

  loadBranch() {
    if (!this.branchId()) return;

    this.isLoading.set(true);

    forkJoin({
      branch: this.branchService.getBranchById(this.branchId()),
      locations: this.locationService.getAllLocations({ branch_ids: [this.branchId()] }),
    }).subscribe({
      next: ({ branch, locations }) => {
        this.branch.set(branch);
        this.allBranchLocations.set(locations.items);
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  loadShrinkData() {
    if (!this.branchId()) return;

    const dateTime = DateTime.fromJSDate(this.monthAndYear());
    this.branchService
      .getBranchLocationsShrink(this.branchId(), {
        month: dateTime.month,
        year: dateTime.year,
      })
      .subscribe({
        next: (resp) => {
          this.shrinkData.set(
            resp.shrinks.map((item) => ({
              id: item.id,
              locationId: item.location.id,
              externalId: item.location.external_id,
              locationName: item.location.name,
              shrinkPercent: item.percentage,
            })),
          );
        },
        error: (err) => {
          this.notifyService.notify(tryGetErrorMessage(err));
        },
      });
  }

  addLocationShrink() {
    if (!this.shrinkAddLocation()) return;
    const dateTime = DateTime.fromJSDate(this.monthAndYear());
    this.branchService
      .createBranchLocationShrink(this.branchId(), this.shrinkAddLocation().id, {
        month: dateTime.month,
        year: dateTime.year,
        percentage: this.shrinkAddPercentage() ?? 0,
      })
      .subscribe({
        next: (resp) => {
          this.shrinkData.update((prev) => [
            ...prev,
            {
              id: resp.id,
              locationId: resp.location.id,
              externalId: resp.location.external_id ?? '',
              locationName: resp.location.name,
              shrinkPercent: resp.percentage,
            },
          ]);
          this.notifyService.notify('Shrink added successfully');
        },
        complete: () => {
          this.shrinkAddLocation.set(undefined);
          this.shrinkAddPercentage.set(undefined);
        },
        error: (err) => {
          this.notifyService.notify(tryGetErrorMessage(err));
        },
      });
  }

  @Debounce(100)
  updateShrink(shrink: BranchShrinkData) {
    this.branchService
      .updateBranchLocationShrink(this.branchId(), shrink.locationId, shrink.id, {
        percentage: shrink.shrinkPercent,
      })
      .subscribe({
        error: (err) => {
          this.notifyService.notify(tryGetErrorMessage(err));
        },
      });
  }

  deleteShrink(shrink: BranchShrinkData) {
    this.branchService.deleteBranchLocationShrink(this.branchId(), shrink.locationId, shrink.id).subscribe({
      next: () => {
        this.shrinkData.update((prev) => prev.filter((item) => item.id !== shrink.id));
        this.notifyService.notify('Shrink removed successfully');
      },
      error: (err) => {
        this.notifyService.notify(tryGetErrorMessage(err));
      },
    });
  }

  closeDatePickerEarly(ev) {
    this.monthAndYear.set(ev.date);

    ev.datePicker['_destroyOverlay']();
    this.loadShrinkData();
  }
}
